
import { useCallback, useEffect, useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { Outlet, useLocation } from 'react-router-dom'
import { Layout } from 'antd'
import { useIsMobile, useIsTablet } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'

import Header from './Header'
import SideBar from './SideBar'
import { SidebarProvider } from './SidebarContext'
import { PracticeProvider } from '../../context/PracticeContext'
import Downloads from '../Downloads'
import OfficeCheckDownloads from '../OfficeCheckDownloads'
import PreAuthorizationDownloads from '../PreAuthorizationDownloads'
import PatientsExcel from '../PatientsExcel'

const { Content } = Layout

export default function AppLayout() {
  const theme = useTheme()
  const location = useLocation()

  const useStyles = createUseStyles({
    outer: {
      minHeight: '100vh',
      '& .ant-layout-sider': {
        minHeight: '100vh',
      },
    },
    content: {
      margin: '24px 16px',
      padding: 24,
      background: ({ theme: th }) => th.brandBackground,
      minHeight: 280,
      [MOBILE_MEDIA_QUERY]: {
        margin: '12px 8px',
        padding: 12,
      },
    },
    backdrop: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.45)',
      zIndex: 999,
    },
  })
  const classes = useStyles({ theme, location })

  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)
  const toggleSidebar = useCallback(() => setIsSidebarVisible(prev => !prev), [])
  const closeSidebar = useCallback(() => setIsSidebarVisible(false), [])

  // Open on desktop, off-canvas on mobile, and folded to the icon rail on a
  // tablet — there the full sidebar costs a quarter of the width the content
  // needs. Re-syncs whenever the viewport crosses a breakpoint.
  useEffect(() => {
    setIsSidebarVisible(!isMobile && !isTablet)
  }, [isMobile, isTablet])

  // On mobile, navigating closes the off-canvas drawer.
  useEffect(() => {
    if (isMobile) setIsSidebarVisible(false)
  }, [location.pathname, isMobile])

  // Lock page scroll behind the open mobile drawer.
  useEffect(() => {
    if (!(isMobile && isSidebarVisible)) return undefined
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobile, isSidebarVisible])

  return (
    <PracticeProvider>
      <Layout className={classes.outer}>
        <SideBar
          collapsed={!isSidebarVisible}
          isMobile={isMobile}
          onClose={closeSidebar}
        />
        {isMobile && isSidebarVisible && (
          <div
            className={classes.backdrop}
            onClick={closeSidebar}
            role="presentation"
          />
        )}
        <Layout>
          <Header
            collapsed={!isSidebarVisible}
            handleToggleCollapsed={toggleSidebar}
            collapsedWidth={70}
          />
          <Content className={classes.content}>
            <SidebarProvider setIsSidebarVisible={setIsSidebarVisible} isSidebarVisible={isSidebarVisible}>
              <Outlet />
            </SidebarProvider>
          </Content>
          <Downloads />
          <OfficeCheckDownloads />
          <PreAuthorizationDownloads />
          <PatientsExcel />
        </Layout>
      </Layout>
    </PracticeProvider>
  )
}
